import { useAppDispatch, useAppSelector } from 'store/hooks'
import { setAppMode, setIsMobile, setIsTablet } from 'store/general-slice'
import { useEffect, useMemo } from 'react'
import { isMobileWidth, isTabletWidth } from './store/resolutions'

import 'styles/_fonts.scss'
import 'styles/_icons.scss'
import Creator from 'Creator'
import Selfcare from 'Selfcare'
import GlobalPages from 'GlobalPages'
import { AppMode } from 'helpers/types'
import { useApp } from 'store/steps'
import { Maintenance } from 'routes/Maintenance'
import moment from 'moment'
import 'moment/min/locales'
import { getDayNames } from 'components/shared/Calendar/hooks'
import {
    CREATOR_ACCOMMODATION_STEPS,
    GLOBAL_STEPS,
    SELFCARE_STEPS,
    STEPS,
    UNLIMITED_CARD_STEPS,
    UNLIMITED_RENEW_STEPS,
    VOUCHERS_STEPS,
} from 'routes/steps'
import { useLocation } from 'react-router-dom'
import Page from 'components/global/Page'
import UnlimitedCard from 'UnlimitedCard'
import UnlimitedRenew from 'UnlimitedRenew'
import CreatorAccommodation from 'CreatorAccommodation'
import { removeTrailingSlashFromPathName } from 'helpers/helpers'
import Vouchers from 'Vouchers'
import FullPageLoader from 'components/global/FullPageLoader'
import { SelfcareAuthProvider } from 'routes/selfcare/auth/SelfcareAuthProvider'
import { usePromotions } from 'components/shared/Promotions/utils/usePromotions'
import { PROMO_ID_BIRTHDAY } from 'components/shared/Promotions/constants'
import { isCurrentDateWithinRange } from 'helpers/dates'
import { ENV_LOCAL, ENV_TEST } from 'constants/system'

type AppProps = {
    appMode: AppMode
}
const App = ({ appMode }: AppProps) => {
    const dispatch = useAppDispatch()
    const { language } = useApp()
    const { pathname } = useLocation()

    const { isAppAccessible, statusCode } = useAppSelector(
        (state) => state.general
    )

    // set ID of current promotion that is based on rules from api
    // or just leave it undefined
    // ! based on past promotions:
    // - consider using conditions that take into account estimated start/end(especially) dates
    const currentPromotionId: string | undefined = isCurrentDateWithinRange(
        [ENV_LOCAL, ENV_TEST].includes(process.env.REACT_APP_ENV || '')
            ? '2025-02-10' // local, test - start calling api already
            : '2025-02-20', // stage, production - start calling api at start time
        '2025-02-26' // end date for calling api
    )
        ? PROMO_ID_BIRTHDAY
        : undefined

    const { isLoading: isPromotionLoading } = usePromotions(
        appMode !== 'global' ? currentPromotionId : undefined
    )

    const AccessibleApp = useMemo(() => {
        switch (appMode) {
            case 'creator-accommodation':
                return <CreatorAccommodation />
            case 'selfcare':
                return (
                    <SelfcareAuthProvider>
                        <Selfcare />
                    </SelfcareAuthProvider>
                )
            case 'unlimited-card':
                return <UnlimitedCard />
            case 'unlimited-renew':
                return <UnlimitedRenew />
            case 'vouchers':
                return <Vouchers />
            case 'global':
                return <GlobalPages />
            default:
                return <Creator />
        }
    }, [appMode])

    useEffect(() => {
        const isValidStep = [
            ...STEPS,
            ...CREATOR_ACCOMMODATION_STEPS,
            ...SELFCARE_STEPS,
            ...UNLIMITED_CARD_STEPS,
            ...UNLIMITED_RENEW_STEPS,
            ...VOUCHERS_STEPS,
            ...GLOBAL_STEPS,
        ].some(
            ({ slug, subPaths }) =>
                Object.values(slug).indexOf(
                    removeTrailingSlashFromPathName(pathname)
                ) > -1 || subPaths?.includes(pathname)
        )

        dispatch(setAppMode(isValidStep ? appMode : 'error'))

        moment.locale(language)
        moment.updateLocale(language, {
            weekdaysShort: getDayNames(language),
        })
    }, [appMode])

    // window resizing
    useEffect(() => {
        const handler = () => {
            dispatch(setIsTablet(isTabletWidth()))
            dispatch(setIsMobile(isMobileWidth()))
        }

        window.addEventListener('resize', handler)

        return () => {
            window.removeEventListener('resize', handler)
        }
    }, [])

    // rendering
    if (isPromotionLoading) return <FullPageLoader />

    if (!isAppAccessible)
        return (
            <Page isFullWidth>
                <Maintenance status={statusCode} />
            </Page>
        )

    return AccessibleApp
}

export default App
